import Arrow from '@/app/components/search/components/Filters/HorizontalFilters/Arrow';
import React, { useImperativeHandle, useRef } from 'react';
import { useIsVisible } from '@/app/common/hooks/useIsVisible';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { Boundary, InnerContainer, OuterContainer } from './styled.ts';

export type ScrollContainerHandle = { scrollToStart: () => void };

type ScrollContainerProps = {
  style?: React.CSSProperties;
  styleInner?: React.CSSProperties;
  children?: React.ReactNode;
  // MPH-398: opt out of the scroll-affordance arrows (the Evo filters bar scrolls by touch only)
  hideArrows?: boolean;
  ref?: React.Ref<ScrollContainerHandle>;
};

type VisibleChildProps = {
  child: React.ReactNode;
  isLast?: boolean;
};

const Child = ({ child, isLast }: VisibleChildProps) => {
  const childRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(childRef, 0.5);

  return (
    <div ref={childRef} aria-hidden={!isVisible} className={isLast ? 'last' : undefined}>
      {child}
    </div>
  );
};

const ScrollContainer = ({ style, styleInner, children, hideArrows, ref }: ScrollContainerProps) => {
  const pillsContainerRef = useRef<HTMLDivElement>(null);

  // Not smooth: the re-render that reorders the pills cancels an animation mid-flight, leaving the
  // bar wherever it had got to.
  useImperativeHandle(
    ref,
    () => ({
      scrollToStart: () => pillsContainerRef.current?.scroll({ left: 0, behavior: 'instant' }),
    }),
    []
  );

  const startBoundaryRef = useRef<HTMLDivElement>(null);
  const endBoundaryRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const childrenArray = React.Children.toArray(children);
  const isStartVisible = useIsVisible(startBoundaryRef, 0.1, pillsContainerRef);
  const isEndVisible = useIsVisible(endBoundaryRef, 0.1, pillsContainerRef);

  const handleArrow = (val: string) => {
    if (val === 'left') {
      pillsContainerRef?.current?.scroll({
        left: pillsContainerRef.current.scrollLeft - 100,
        behavior: 'smooth',
      });
    } else {
      pillsContainerRef?.current?.scroll({
        left: pillsContainerRef.current.scrollLeft + 100,
        behavior: 'smooth',
      });
    }
  };

  return (
    <OuterContainer style={style}>
      {!hideArrows && !isStartVisible && (
        <Arrow handleArrowClick={handleArrow} noShadow={false} aria-label={t('vacation.oss.filters.show_more')} />
      )}
      <InnerContainer ref={pillsContainerRef} id="pills-scrollable" style={styleInner}>
        <Boundary ref={startBoundaryRef} className="start-boundary" />
        {childrenArray.map((child, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Child key={index} child={child} isLast={index === childrenArray.length - 1} />
        ))}
        <Boundary ref={endBoundaryRef} className="end-boundary" />
      </InnerContainer>
      {!hideArrows && !isEndVisible && (
        <Arrow
          handleArrowClick={handleArrow}
          noShadow={false}
          isRightArrow={true}
          aria-label={t('vacation.oss.filters.show_more')}
        />
      )}
    </OuterContainer>
  );
};

export default ScrollContainer;
